import { aiJson } from "@/services/ai.service";
import type { ExamPaper } from "./paper";

/**
 * Korrektur einer Probeklausur – so, wie eine Lehrkraft sie zurückgibt:
 * Randnotizen am Text (einzelne Wörter oder Passagen unterstrichen, daneben
 * das Kürzel und die Anmerkung), darunter die Bewertungsmatrix und das
 * Schlusswort.
 *
 * Der Unterschied zur reinen Prozentzahl: Man sieht, WO etwas gut oder falsch
 * war. Genau das macht die Verbesserung sichtbar.
 */

/** Übliche Korrekturzeichen aus dem Deutschunterricht. */
export const MARK_MEANING: Record<string, string> = {
  "+": "gut gemacht",
  "++": "sehr gut",
  R: "Rechtschreibung",
  Z: "Zeichensetzung",
  Gr: "Grammatik",
  A: "Ausdruck",
  Sb: "Satzbau",
  W: "Wortwiederholung",
  I: "inhaltlich falsch",
  Bz: "Bezug unklar",
  Th: "Thema verfehlt",
  Bg: "Begründung fehlt",
  Bl: "Beleg fehlt",
  "?": "unklar formuliert",
};

export type MarkKey = keyof typeof MARK_MEANING;

export interface MarginNote {
  /** Wörtlicher Ausschnitt aus der Schülerantwort – wird unterstrichen. */
  quote: string;
  mark: string;
  note: string;
  /** Bessere Formulierung, falls sinnvoll. */
  better?: string;
}

export type GradeMark = "++" | "+" | "o" | "-" | "--";

export interface GradeRow {
  criterion: string;
  /** Übergeordneter Block, z. B. „Inhalt" oder „Sprache". */
  area: string;
  mark: GradeMark;
  points: number;
  maxPoints: number;
  comment: string;
}

export interface ExamGrade {
  grade: string;
  percent: number;
  points: number;
  maxPoints: number;
  rows: GradeRow[];
  notes: MarginNote[];
  strengths: string;
  improve: string[];
  summary: string;
  answer?: string;
  topicId?: string;
  topicTitle?: string;
}

/**
 * Bewertungsbereiche. Vorher waren es 4–6 frei gewählte Kriterien; jetzt gibt
 * es feste Blöcke mit je zwei bis drei Kriterien, damit zwei Klausuren
 * überhaupt vergleichbar sind und man über die Zeit eine Entwicklung sieht.
 */
const AREAS = `BEWERTUNGSBEREICHE (genau diese, in dieser Reihenfolge):
Inhalt:  "Aufgabenbezug & Operator", "Fachliche Richtigkeit", "Vollständigkeit", "Belege am Material"
Methode: "Analyse-/Argumentationstiefe", "Eigenständigkeit des Urteils"
Sprache: "Fachsprache", "Ausdruck & Stil", "Satzbau & Grammatik", "Rechtschreibung & Zeichensetzung"
Form:    "Aufbau & Gliederung", "Zitierweise & Belegtechnik"
Das ergibt 12 Zeilen. Verteile die Gesamtpunkte sinnvoll auf sie (Inhalt am schwersten).`;

export async function gradeExam(paper: ExamPaper, answer: string): Promise<ExamGrade> {
  const maxPoints = paper.tasks.reduce((n, t) => n + t.points, 0);
  const tasksDesc = paper.tasks
    .map((t, i) => `Aufgabe ${i + 1} (AFB ${t.afb}, Operator „${t.operator}", ${t.points} P.):\n${t.question}\nErwartung: ${t.expectation}`)
    .join("\n\n");

  const marks = Object.entries(MARK_MEANING)
    .map(([k, v]) => `${k} = ${v}`)
    .join(", ");

  const result = await aiJson<ExamGrade>(
    "Du bist eine erfahrene, wohlwollende Lehrkraft und korrigierst eine Probeklausur so, wie du " +
      "sie zurückgeben würdest: mit Randnotizen am Text, einer Bewertungsmatrix und einem " +
      "handschriftlichen Schlusswort.\n\n" +
      "RANDNOTIZEN (Feld \"notes\"): 12–20 Stück, gleichmäßig über den GANZEN Text verteilt – " +
      "auch im letzten Drittel. Jede nennt in \"quote\" einen WÖRTLICHEN, eindeutigen Ausschnitt " +
      "aus der Schülerantwort (3–12 Wörter, exakt so wie er dasteht), dazu ein Kürzel und eine " +
      "kurze Anmerkung wie am Rand eines Blattes (max. 12 Wörter).\n" +
      `Erlaubte Kürzel: ${marks}.\n` +
      "Verteilung: etwa 3–4 Lob (+ / ++) an konkreten starken Stellen, der Rest Kritik – " +
      "inhaltliche Fehler, fehlende Belege, unklare Bezüge, Sprache. Sei genau und ehrlich, " +
      "beschönige nichts. Bei Sprachfehlern gehört in \"better\" die richtige Fassung.\n\n" +
      "ZITIERWEISE ist ein eigenes Bewertungskriterium und ein häufiger Anlass für Randnotizen. " +
      "Korrekt sind Stellenangaben in Klammern direkt hinter der Aussage:\n" +
      "- Verse: (V. 3), (V. 12 f.), (V. 5 ff.), (V. 1–4)\n" +
      "- Zeilen in Prosa/Sachtexten: (Z. 14–16)\n" +
      "- mehrere Materialien: (M2, V. 15 f.)\n" +
      "- sinngemäß statt wörtlich: (vgl. V. 7)\n" +
      "- englischsprachige Klausur: (l. 14-16), (ll. 3-5), (cf. l. 7)\n" +
      "Fehlt eine Stellenangabe hinter einem Zitat oder einer Textaussage, ist das eine Randnotiz " +
      'mit Kürzel "Bl". Steht sie falsch (z. B. „Zeile 3" ausgeschrieben, „S. 2" bei einem Gedicht, ' +
      'Angabe vor statt hinter der Aussage, fehlendes „vgl." bei sinngemäßer Wiedergabe), ist das ' +
      'eine Randnotiz mit Kürzel "Z" und der richtigen Fassung in "better".\n\n' +
      `${AREAS}\n\n` +
      "Bei interpretationsoffenen Aufgaben gilt: Der Erwartungshorizont ist EINE mögliche Lösung. " +
      "Jede fachlich vertretbare, am Material belegte Position zählt gleichwertig.\n\n" +
      "Gib NUR JSON zurück:\n" +
      '{"grade":"Schulnote wie \\"2+\\"","percent":0-100,"points":erreichte Punkte,' +
      `"maxPoints":${maxPoints},` +
      '"rows":[{"area":"Inhalt","criterion":"…","mark":"++|+|o|-|--","points":0,"maxPoints":0,"comment":"ein Satz"}],' +
      '"notes":[{"quote":"…","mark":"R","note":"…","better":"…"}],' +
      '"strengths":"1-2 Sätze","improve":["konkrete Übe-Tipps", …3],' +
      '"summary":"3-5 Sätze Gesamtfeedback, persönlich und ermutigend"}',
    // Antwort gedeckelt (war unbegrenzt) – zusammen mit dem umfangreichen
    // System-Prompt und max_tokens muss die Anfrage unter Groqs
    // Minuten-Kontingent bleiben (siehe GROQ_SAFE_PROMPT_TOKENS).
    `MATERIAL:\n${paper.source}\n\nQUELLE: ${paper.citation}\n\nAUFGABEN:\n${tasksDesc}\n\nSCHÜLERANTWORT:\n${answer.slice(0, 4000)}`,
    3000,
    "medium"
  );

  return normalizeGrade(result, answer, maxPoints);
}

function normalizeGrade(g: ExamGrade, answer: string, maxPoints: number): ExamGrade {
  const validMarks = new Set(["++", "+", "o", "-", "--"]);
  const rows = (Array.isArray(g.rows) ? g.rows : [])
    .map((r) => ({
      area: String(r?.area ?? "Inhalt").trim(),
      criterion: String(r?.criterion ?? "").trim(),
      mark: (validMarks.has(String(r?.mark)) ? String(r.mark) : "o") as GradeMark,
      points: Math.max(0, Number(r?.points) || 0),
      maxPoints: Math.max(0, Number(r?.maxPoints) || 0),
      comment: String(r?.comment ?? "").trim(),
    }))
    .filter((r) => r.criterion);

  // Nur Notizen behalten, deren Zitat wirklich im Text steht – sonst lässt es
  // sich nicht markieren und der Rand zeigt auf nichts.
  const notes = (Array.isArray(g.notes) ? g.notes : [])
    .map((n) => ({
      quote: String(n?.quote ?? "").trim(),
      mark: String(n?.mark ?? "?").trim(),
      note: String(n?.note ?? "").trim(),
      better: String(n?.better ?? "").trim() || undefined,
    }))
    .filter((n) => n.quote && findQuote(answer, n.quote) >= 0)
    .slice(0, 22);

  const points = Number(g.points);
  const sumRows = rows.reduce((n, r) => n + r.points, 0);
  const got = Number.isFinite(points) && points > 0 ? points : sumRows;
  const max = Number(g.maxPoints) > 0 ? Number(g.maxPoints) : maxPoints;
  const percent =
    Number.isFinite(Number(g.percent)) && Number(g.percent) > 0
      ? Math.max(0, Math.min(100, Math.round(Number(g.percent))))
      : max > 0
        ? Math.round((got / max) * 100)
        : 0;

  return {
    grade: String(g.grade ?? "").trim() || gradeFromPercent(percent),
    percent,
    points: Math.round(got),
    maxPoints: max,
    rows,
    notes,
    strengths: String(g.strengths ?? "").trim(),
    improve: (Array.isArray(g.improve) ? g.improve : []).map((s) => String(s).trim()).filter(Boolean).slice(0, 5),
    summary: String(g.summary ?? "").trim(),
    answer,
  };
}

/** Notenschlüssel wie in der Oberstufe (Notenpunkte -> Schulnote). */
function gradeFromPercent(p: number): string {
  if (p >= 95) return "1+";
  if (p >= 90) return "1";
  if (p >= 85) return "1-";
  if (p >= 80) return "2+";
  if (p >= 75) return "2";
  if (p >= 70) return "2-";
  if (p >= 65) return "3+";
  if (p >= 60) return "3";
  if (p >= 55) return "3-";
  if (p >= 50) return "4+";
  if (p >= 45) return "4";
  if (p >= 40) return "4-";
  if (p >= 27) return "5";
  return "6";
}

/**
 * Zitat im Text finden – erst wörtlich, dann tolerant gegenüber
 * Leerzeichen und Groß-/Kleinschreibung. Sprachmodelle normalisieren beim
 * Zitieren gern, und dann fände ein reines `indexOf` nichts.
 */
export function findQuote(text: string, quote: string): number {
  const direct = text.indexOf(quote);
  if (direct >= 0) return direct;

  const needle = quote.toLowerCase().replace(/\s+/g, " ").trim();
  const hay = text.toLowerCase();
  // Über den Text laufen und dabei Whitespace-Folgen wie ein Leerzeichen
  // behandeln, damit Zeilenumbrüche in der Antwort nicht stören.
  let ni = 0;
  let start = -1;
  for (let i = 0; i < hay.length && ni < needle.length; i++) {
    const hc = /\s/.test(hay[i]) ? " " : hay[i];
    const nc = needle[ni];
    if (hc === nc || (nc === " " && hc === " ")) {
      if (ni === 0) start = i;
      ni++;
      // Mehrfach-Whitespace im Text überspringen
      while (nc === " " && i + 1 < hay.length && /\s/.test(hay[i + 1])) i++;
    } else if (ni > 0) {
      i = start;
      ni = 0;
      start = -1;
    }
  }
  return ni === needle.length ? start : -1;
}

export interface Segment {
  text: string;
  note?: MarginNote;
  index?: number;
}

/**
 * Die Schülerantwort in Abschnitte zerlegen: normale Textstücke und markierte
 * Stellen mit ihrer Randnotiz. Überlappende Treffer werden verworfen, sonst
 * würden zwei Unterstreichungen ineinander laufen.
 */
export function segmentAnswer(answer: string, notes: MarginNote[]): Segment[] {
  const hits: { start: number; end: number; note: MarginNote }[] = [];
  for (const note of notes) {
    const start = findQuote(answer, note.quote);
    if (start < 0) continue;
    const end = start + matchedLength(answer, start, note.quote);
    if (hits.some((h) => start < h.end && end > h.start)) continue;
    hits.push({ start, end, note });
  }
  hits.sort((a, b) => a.start - b.start);

  const out: Segment[] = [];
  let cursor = 0;
  hits.forEach((hit, i) => {
    if (hit.start > cursor) out.push({ text: answer.slice(cursor, hit.start) });
    out.push({ text: answer.slice(hit.start, hit.end), note: hit.note, index: i + 1 });
    cursor = hit.end;
  });
  if (cursor < answer.length) out.push({ text: answer.slice(cursor) });
  return out;
}

/** Wie viele Zeichen der Treffer im Original tatsächlich einnimmt. */
function matchedLength(text: string, start: number, quote: string): number {
  if (text.startsWith(quote, start)) return quote.length;
  const needle = quote.toLowerCase().replace(/\s+/g, " ").trim();
  let ni = 0;
  let i = start;
  for (; i < text.length && ni < needle.length; i++) {
    const hc = /\s/.test(text[i]) ? " " : text[i].toLowerCase();
    if (hc === needle[ni]) {
      ni++;
      while (needle[ni - 1] === " " && i + 1 < text.length && /\s/.test(text[i + 1])) i++;
    }
  }
  return i - start;
}
